import React from 'react'

import {Box, Theme} from '@mui/material'
import {PGButton, PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

export interface OverviewCardProps {
  icon?: React.ComponentType<any>
  title?: string
  content: React.ReactNode
  minHeight?: number
  primaryButton?: {
    label: string
    onClick: () => void
    disabled?: boolean
  }
  secondaryButton?: {
    label: string
    onClick: () => void
    disabled?: boolean
  }
  color?: 'white' | 'bright' | 'dark'
  disabled?: boolean
  locked?: boolean
  colors: any // Product line colors from getProductLineColors
  isFullRow?: boolean
}

const useStyles = makeStyles<{
  color: 'white' | 'bright' | 'dark'
  colors: any
  locked?: boolean
  minHeight?: number
  isFullRow?: boolean
}>()((theme: Theme, {color, colors, locked, minHeight, isFullRow}) => {
  return {
    card: {
      display: 'flex',
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '32px',
      gap: '32px',
      background: getBackgroundColor(color, colors),
      boxShadow: '0px 3px 16px rgba(190, 192, 198, 0.24)',
      borderRadius: '16px',
      flex: 'none',
      order: 0,
      flexGrow: 0,
      filter: locked ? 'grayscale(100%)' : 'none',
      opacity: locked ? 0.5 : 1,
      pointerEvents: locked ? 'none' : 'auto',
      minHeight: minHeight || undefined,
    },
    contentSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      flex: 1,
      width: '100%',
      justifyContent: minHeight ? 'center' : undefined,

      '@container (min-width: 800px)': {
        flexDirection: 'row',
        alignItems: 'center',
      },
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: minHeight ? 'center' : 'flex-start',
      justifyContent: minHeight ? 'center' : 'flex-start',
    },
    iconFrame: {
      boxSizing: 'border-box',
      width: '44px',
      height: '44px',
      background: colors.secondary || '#F1FCFE',
      border: `1px solid ${colors.primary || '#B3E1EA'}`,
      borderRadius: '1000px',
      flex: 'none',
      order: 0,
      flexGrow: 0,
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: '20px',
      height: '20px',
      flexShrink: 0,
    },
    content: {
      width: '100%',
      maxWidth: '860px',
      color: '#251F1F',
      '& ul': {
        paddingLeft: '18px',
        marginTop: 0,
      },
      '& p': {
        margin: '2px 0',
      },
    },
    buttonContainer: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      flexShrink: 0,
      marginTop: isFullRow ? undefined : 'auto',
    },
    primaryButton: {
      '&.Mui-disabled': {
        opacity: 0.3,
        backgroundColor: `${colors.primary} !important`,
        color: '#FFFFFF !important',
      },
    },
  }
})

function getBackgroundColor(
  color: 'white' | 'bright' | 'dark',
  colors: any
): string {
  switch (color) {
    case 'white':
      return '#FFFFFF'
    case 'bright':
      return colors.secondary
    case 'dark':
      return colors.secondary
    default:
      return '#FFFFFF'
  }
}

export const OverviewCard: React.FC<OverviewCardProps> = ({
  icon: IconComponent,
  title,
  content,
  primaryButton,
  secondaryButton,
  color = 'white',
  disabled = false,
  locked = false,
  colors,
  minHeight,
  isFullRow = false,
}) => {
  const {classes} = useStyles({color, colors, locked, minHeight, isFullRow})

  return (
    <Box className={classes.card} sx={{containerType: 'inline-size'}}>
      <Box className={classes.contentSection}>
        <Box className={classes.header}>
          {(IconComponent || title) && (
            <Box>
              {IconComponent && (
                <Box
                  className={classes.iconFrame}
                  sx={{color: colors?.primary || '#148199'}}
                >
                  <IconComponent
                    className={classes.icon}
                    sx={{
                      color: 'inherit',
                      fontSize: '24px',
                    }}
                  />
                </Box>
              )}
              {title && <PGTypography variant='title'>{title}</PGTypography>}
            </Box>
          )}

          <Box className={classes.content}>{content}</Box>
        </Box>

        {/* Buttons shown at bottom on narrow cards */}
        {(primaryButton || secondaryButton) && (
          <Box className={classes.buttonContainer}>
            {secondaryButton && (
              <PGButton
                onClick={
                  disabled || secondaryButton.disabled
                    ? undefined
                    : secondaryButton.onClick
                }
                size='medium'
                disabled={disabled || secondaryButton.disabled}
                type='button'
                variant='text'
                label={secondaryButton.label}
              />
            )}

            {primaryButton && (
              <PGButton
                onClick={
                  disabled || primaryButton.disabled
                    ? undefined
                    : primaryButton.onClick
                }
                size='medium'
                disabled={disabled || primaryButton.disabled}
                label={primaryButton.label}
                className={classes.primaryButton}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default OverviewCard
